import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Box, Heading, Text, Button } from '@indeed/ifl-components';
import { GettextInterface } from '@indeed/indeed-gettext';
import PageTransition from '../../../components/PageTransition/PageTransition';
import JobDescription from '../../../components/JobDescription/JobDescription';
import { usePageFlow } from '../../../lib/pageFlow/usePageFlow';

export interface ProfilePicturePageProps {
    step?: string;
    gt?: GettextInterface;
}

const ProfilePicturePage: React.FunctionComponent<ProfilePicturePageProps> = () => {
    const { goBack, canGoBack, goForward } = usePageFlow();
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [uploadError, setUploadError] = useState<string>('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setUploadError('Please select an image file (JPG, PNG, GIF)');
            return;
        }

        // Validate file size (5MB limit)
        if (file.size > 5 * 1024 * 1024) {
            setUploadError('File size must be less than 5MB');
            return;
        }

        setUploadError('');
        setSelectedFile(file);

        // Create preview URL
        const reader = new FileReader();
        reader.onload = () => {
            setPreviewUrl(reader.result as string);
        };
        reader.readAsDataURL(file);
    };

    const handleFileUploadClick = (): void => {
        fileInputRef.current?.click();
    };

    const handleRemovePhoto = (): void => {
        setSelectedFile(null);
        setPreviewUrl(null);
        setUploadError('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleBack = (): void => {
        void goBack();
    };

    const handleContinue = (): void => {
        // Profile picture is optional, so we can continue regardless
        void goForward();
    };

    const handleSkip = (): void => {
        void goForward();
    };

    return (
        <PageTransition pageKey="profile-picture">
            <Helmet>
                <title>Profile Picture - Indeed</title>
                <meta
                    name="description"
                    content="Upload a professional profile picture to enhance your job application."
                />
                <meta
                    name="keywords"
                    content="profile picture, photo upload, job application, Indeed, professional photo"
                />
            </Helmet>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: ['1fr', '1fr 1fr'],
                    gap: 6,
                    alignItems: 'start',
                }}
            >
                {/* Left Section - Profile Picture Upload */}
                <Box
                    sx={{
                        backgroundColor: 'background.default',
                        padding: 6,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'border.subtle',
                    }}
                >
                    <Heading
                        as="h2"
                        level={2}
                        sx={{
                            color: 'foreground.primary',
                            marginBlockEnd: 2,
                        }}
                    >
                        Profile Picture
                    </Heading>
                    <Text sx={{ color: 'foreground.secondary', fontSize: 2, marginBlockEnd: 6 }}>
                        Add a professional photo to make your application stand out. This step is
                        optional.
                    </Text>

                    {/* Photo Upload Area */}
                    <Box sx={{ marginBlockEnd: 6 }}>
                        {previewUrl ? (
                            // Show preview if photo is selected
                            <Box sx={{ textAlign: 'center', marginBlockEnd: 4 }}>
                                <Box
                                    sx={{
                                        inlineSize: '200px',
                                        blockSize: '200px',
                                        borderRadius: '50%',
                                        overflow: 'hidden',
                                        margin: '0 auto',
                                        border: '3px solid',
                                        borderColor: 'primary.default',
                                        marginBlockEnd: 4,
                                    }}
                                >
                                    <img
                                        src={previewUrl}
                                        alt="Profile preview"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                        }}
                                    />
                                </Box>
                                <Text sx={{ marginBlockEnd: 3, fontWeight: 'semibold' }}>
                                    {selectedFile?.name}
                                </Text>
                                <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center' }}>
                                    <Button variant="secondary" onClick={handleFileUploadClick}>
                                        Change Photo
                                    </Button>
                                    <Button variant="tertiary" onClick={handleRemovePhoto}>
                                        Remove Photo
                                    </Button>
                                </Box>
                            </Box>
                        ) : (
                            // Show upload area if no photo is selected
                            <Box
                                onClick={handleFileUploadClick}
                                sx={{
                                    border: '2px dashed',
                                    borderColor: 'border.subtle',
                                    borderRadius: 2,
                                    padding: 8,
                                    textAlign: 'center',
                                    backgroundColor: 'background.subtle',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        borderColor: 'primary.default',
                                        backgroundColor: 'primary.subtle',
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        inlineSize: '100px',
                                        blockSize: '100px',
                                        borderRadius: '50%',
                                        backgroundColor: 'background.secondary',
                                        margin: '0 auto',
                                        marginBlockEnd: 4,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '48px',
                                        color: 'foreground.secondary',
                                    }}
                                >
                                    📷
                                </Box>
                                <Text
                                    sx={{
                                        color: 'foreground.primary',
                                        marginBlockEnd: 3,
                                        fontWeight: 'semibold',
                                    }}
                                >
                                    Upload Profile Picture
                                </Text>
                                <Text sx={{ color: 'foreground.secondary', marginBlockEnd: 3 }}>
                                    Drag and drop your photo here, or click to browse
                                </Text>
                                <Button variant="secondary">Choose Photo</Button>
                            </Box>
                        )}

                        {/* Hidden file input */}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleFileSelect}
                            style={{ display: 'none' }}
                        />

                        {/* Error message */}
                        {uploadError && (
                            <Text
                                sx={{
                                    color: 'danger.default',
                                    fontSize: 1,
                                    marginBlockStart: 3,
                                    textAlign: 'center',
                                }}
                            >
                                {uploadError}
                            </Text>
                        )}
                    </Box>

                    {/* Photo Guidelines */}
                    <Box sx={{ marginBlockEnd: 6 }}>
                        <Heading
                            as="h3"
                            level={4}
                            sx={{ color: 'foreground.primary', marginBlockEnd: 3 }}
                        >
                            Photo Guidelines
                        </Heading>
                        <Box as="ul" sx={{ paddingInlineStart: 4 }}>
                            <Text as="li" sx={{ color: 'foreground.secondary', marginBlockEnd: 2 }}>
                                Use a professional, recent photo of yourself
                            </Text>
                            <Text as="li" sx={{ color: 'foreground.secondary', marginBlockEnd: 2 }}>
                                Face should be clearly visible and well-lit
                            </Text>
                            <Text as="li" sx={{ color: 'foreground.secondary', marginBlockEnd: 2 }}>
                                Dress professionally as you would for an interview
                            </Text>
                            <Text as="li" sx={{ color: 'foreground.secondary', marginBlockEnd: 2 }}>
                                Avoid group photos, selfies, or casual photos
                            </Text>
                            <Text as="li" sx={{ color: 'foreground.secondary', marginBlockEnd: 2 }}>
                                Supported formats: JPG, PNG, GIF (max 5MB)
                            </Text>
                        </Box>
                    </Box>

                    {/* Navigation Buttons */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 3,
                            marginBlockStart: 8,
                        }}
                    >
                        <Box>
                            {canGoBack && (
                                <Button variant="secondary" onClick={handleBack}>
                                    Back
                                </Button>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 3 }}>
                            <Button variant="tertiary" onClick={handleSkip}>
                                Skip for Now
                            </Button>
                            <Button variant="primary" onClick={handleContinue}>
                                Continue
                            </Button>
                        </Box>
                    </Box>
                </Box>

                {/* Right Section - Job Description */}
                <JobDescription />
            </Box>
        </PageTransition>
    );
};

export default ProfilePicturePage;
